import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useOnboarding } from "@/context/OnboardingContext";
import { useLang } from "@/context/LanguageContext";
import LanguagePill from "@/components/LanguagePill";
import OnboardingExit from "@/components/OnboardingExit";

export default function OnboardingPriorities() {
  const navigate = useNavigate();
  const { data, update } = useOnboarding();
  const { t } = useLang();
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(data.priorities || []);

  useEffect(() => {
    api.get("/onboarding/options").then(r => setOptions(r.data.priorities)).catch(() => {});
  }, []);

  const toggle = (k) => {
    setSelected(prev => {
      if (prev.includes(k)) return prev.filter(x => x !== k);
      if (prev.length >= 5) return prev;
      return [...prev, k];
    });
  };

  const next = () => {
    update({ priorities: selected, deep_dives: data.deep_dives?.filter(d => selected.includes(d.priority)) || [] });
    navigate("/onboarding/deep-dive/0");
  };

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto" style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 flex-1">
          {[1,2,3,4,5].map(i => <span key={i} className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: i <= 2 ? "var(--vedari-gold)" : "var(--vedari-divider)" }} />)}
        </div>
        <LanguagePill persist />
        <OnboardingExit />
      </div>

      <h1 className="font-display text-4xl mb-2 leading-tight" data-testid="priorities-title">{t("ob.prio.title")}</h1>
      <p className="text-body" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("ob.prio.sub_a")} <span className="key-data">5</span>{t("ob.prio.sub_b")}
      </p>
      <p className="text-body-sm mt-3" style={{ color: "var(--vedari-gold)" }} data-testid="priorities-count">
        {t("ob.prio.count", { a: selected.length })}
      </p>

      <div className="grid grid-cols-2 gap-3 mt-6">
        {options.map(opt => {
          const isSel = selected.includes(opt.key);
          const isDisabled = !isSel && selected.length >= 5;
          return (
            <button
              key={opt.key}
              data-testid={`priority-${opt.key}`}
              onClick={() => toggle(opt.key)}
              disabled={isDisabled}
              className="text-left p-4 rounded-2xl transition-all min-h-[100px] disabled:opacity-40"
              style={{
                backgroundColor: isSel ? "rgba(184,149,74,0.10)" : "var(--vedari-surface)",
                border: `1.5px solid ${isSel ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
              }}
            >
              <div className="text-2xl mb-2">{opt.emoji}</div>
              <div className="text-body-sm leading-snug font-medium">{opt.label}</div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3 mt-8">
        <button onClick={() => navigate(-1)} className="vedari-btn-secondary flex-1" data-testid="priorities-back-btn">{t("common.back")}</button>
        <button onClick={next} disabled={selected.length === 0} className="vedari-btn-primary flex-1" data-testid="priorities-continue-btn">{t("common.continue")}</button>
      </div>
    </div>
  );
}
